<template>
  <bottom-drawer-wrapper :tabTitle="tabInfo.name">
    <template #body>
      <v-row v-if="isLoading" no-gutters class="fill-height">
        <v-col cols="12" v-if="isLoading">
          <main-loader />
        </v-col>
      </v-row>
      <v-row v-else no-gutters class="fill-height" style="overflow-y: hidden; flex-wrap: nowrap;">
        <v-col
          cols="auto"
          class="pa-6"
          style="overflow-y: auto; height: 100%;"
          :class="[isSidebarVisible ? 'tab-col-withsidebar' : 'tab-col-withoutsidebar']"
        >
          <v-expansion-panels v-model="currentSideTab" accordion focusable>
            <template v-for="(tab, index) in expansionPanelTabs">
              <rzi-authorization-list
                v-if="tab.value === 'authorization'"
                :key="index"
                :is-opened="currentSideTab === index"
                :document-data="tabInfo.documentFeature"
                :current-metadata="currentMetadata"
              />
              <v-expansion-panel v-else :key="index">
                <v-expansion-panel-header>{{ tab.title }}</v-expansion-panel-header>
                <v-expansion-panel-content v-if="tab.value === 'general'">
                  <v-row no-gutters class="pr-5 mt-4">
                    <v-spacer></v-spacer>
                    <v-col cols="auto">
                      <v-row no-gutters>
                        <v-col v-if="!isEditingOn">
                          <base-rounded-button
                            @click="isEditingOn = true"
                            color="rgba(235, 235, 235, 0.5)"
                            size="x-small"
                            icon="mdi-pencil"
                            icon-color="#202124"
                          />
                        </v-col>
                        <template v-else>
                          <v-col>
                            <base-rounded-button
                              @click="handleEdit"
                              color="success"
                              size="x-small"
                              :loading="isEditLoading"
                              icon="mdi-check"
                              icon-color="white"
                            />
                          </v-col>
                          <v-col>
                            <base-rounded-button
                              @click="cancelEditing"
                              color="error"
                              :disabled="isEditLoading"
                              size="x-small"
                              icon="mdi-close"
                              icon-color="white"
                            />
                          </v-col>
                        </template>
                      </v-row>
                    </v-col>
                  </v-row>
                  <feature-card-data
                    class="custom-data ml-5"
                    :elements="formSchema"
                    v-model="values"
                    :is-editing-on="isEditingOn"
                    dynamic-icon-column
                    disableWidthLimit
                  />
                </v-expansion-panel-content>
                <v-expansion-panel-content v-else>
                  <rzi-side-lists
                    :tab-info="tabInfo"
                    :current-tab.sync="currentListsTab"
                    :current-metadata="currentMetadata"
                    :items="protectedSiteFeatures"
                    @siteAdded="closeDialog => handleGetItems(closeDialog)"
                    :selected-site.sync="selectedSite"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </template>
          </v-expansion-panels>
        </v-col>
        <v-divider vertical />
        <v-col style="overflow-y: auto; height: 100%;" class="fill-height">
          <rzi-documentation-item-section
            v-if="!!selectedSite"
            :item="selectedSiteItem"
            :current-tab="currentListsTab"
            :tab-info="tabInfo"
            :current-metadata="currentMetadata"
            @itemEdited="closeDialog => handleGetItems(closeDialog)"
            @deleteSite="closeDialog => handleGetItems(closeDialog, 'delete')"
          />
        </v-col>
      </v-row>
    </template>
  </bottom-drawer-wrapper>
</template>

<script>
import BottomDrawerWrapper from '@/components/BottomDrawerWrapper';
import FeatureCardData from '@/components/FeatureCardData';
import MainLoader from '@/components/MainLoader';
import RziSideLists from '@/components/RziSideLists';
import RziDocumentationItemSection from '@/components/RziDocumentationItemSection';
import RziAuthorizationList from '@/components/RziAuthorizationList';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { systemAttributes } from '@/assets/js/variables';
import { utils } from '@/mixins/rziHelpers';

import { documentation, sitesTypes as sitesTypesData } from '@/assets/js/rziData.js';

import { get, call, sync } from 'vuex-pathify';

export default {
  name: 'TabTableRziDocumentation',
  mixins: [prepareAttributesFeatureCardMixin, utils],
  components: {
    BottomDrawerWrapper,
    FeatureCardData,
    MainLoader,
    RziSideLists,
    RziDocumentationItemSection,
    RziAuthorizationList,
  },
  props: {
    tabInfo: {
      type: Object,
    },
  },
  data: () => ({
    values: {},
    isEditingOn: false,
    formSchema: [],
    isLoading: true,
    isEditLoading: false,
    selectedSite: null,
    currentListsTab: 'habitats',
    protectedSiteFeatures: [],
    currentSideTab: null,
  }),
  computed: {
    isSidebarVisible: get('sidebar/isVisible'),
    layers: get('layers/layers'),
    layersMetadata: get('layers/metadata'),
    currentTableTab: get('table/currentTableTab'),
    tableTabs: get('table/tabs'),
    sitesTypesFeatures: sync('rziData/sitesTypesFeatures'),
    currentFeatureId() {
      return this.$route.params.fid;
    },
    selectedSiteItem() {
      return { id: this.selectedSite, feature: this.protectedSiteFeatures.find(item => item.id === this.selectedSite) };
    },
    currentMetadata() {
      return documentation;
    },
    sitesPayload() {
      return {
        dataSource: this.currentMetadata.protectedSites.datasource,
        filters: {
          $EQUAL: {
            [`${this.currentMetadata.protectedSites.datasource}.${this.currentMetadata.protectedSites.attributes.docsId}`]: {
              value: this.tabInfo.documentFeature.id,
            },
          },
        },
      };
    },
    expansionPanelTabs() {
      return ['general', 'items', 'authorization'].map(tab => {
        return {
          value: tab,
          title: this.$i18n.t(`table.rziDocuments.${tab}`),
        };
      });
    },
  },
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    cancelEditing() {
      this.values = JSON.parse(JSON.stringify(this.valuesCopy));
      this.isEditingOn = false;
    },
    async handleEdit() {
      this.isEditLoading = true;
      try {
        await this.editNonTopoFeatureAction({
          datasource: this.currentMetadata.documents.datasource,
          id: this.tabInfo.documentFeature.id,
          body: this.values,
        });
        const currentTab = this.tableTabs.find(tab => tab.value === this.currentTableTab);
        this.$store.set('table/UPDATE_TAB_PROPERTIES!', {
          tabName: this.currentTableTab,
          metadata: {
            name: this.getDocumentTitle(currentTab.documentFormName, this.values),
            documentFeature: {
              ...currentTab.documentFeature,
              properties: Object.fromEntries(
                Object.entries(currentTab.documentFeature.properties).map(([key]) => [key, this.values[key]])
              ),
            },
          },
        });
        this.isEditingOn = false;
      } catch {
        //
      } finally {
        this.isEditLoading = false;
      }
    },
    async handleGetItems(closeDialog, type = 'edit') {
      try {
        const protectedSiteFeatures = await this.getDataSourceFeatures(this.sitesPayload);
        if (type === 'delete') this.selectedSite = null;
        this.protectedSiteFeatures = protectedSiteFeatures.features;
        closeDialog();
      } catch (error) {
        console.error(error);
      }
    },
    async init() {
      this.isLoading = true;
      try {
        this.formSchema = this.getForm(
          null,
          this.layersMetadata[this.currentMetadata.documents.datasource].attributes_schema.attributes,
          {
            filteredOutAttributes: this.layersMetadata[
              this.currentMetadata.documents.datasource
            ].attributes_schema.attributes
              .filter(attr => systemAttributes.includes(attr.name))
              .map(attr => attr.name),
          }
        ).map(attr => {
          return {
            ...attr,
            ...([
              this.currentMetadata.documents.attributes.docType,
              this.currentMetadata.documents.attributes.mainArea,
            ].includes(attr.attribute) && { editable: false }),
          };
        });
        const feature = this.tabInfo.documentFeature;
        const dt = sitesTypesData.metadata.datasource;
        const [sitesTypes, protectedSiteFeatures] = await Promise.all([
          ...(this.sitesTypesFeatures.length
            ? [new Promise(resolve => resolve(null))]
            : [this.getDataSourceFeatures({ dataSource: dt })]),
          this.getDataSourceFeatures(this.sitesPayload),
        ]);
        if (sitesTypes) this.sitesTypesFeatures = sitesTypes.features;
        this.protectedSiteFeatures = protectedSiteFeatures.features;

        const idAttributeName = this.layersMetadata[this.currentMetadata.documents.datasource].attributes_schema
          .id_name;
        const properties = this.getFlattenProperties(JSON.parse(JSON.stringify(feature)), idAttributeName);
        this.values = this.getValues(this.formSchema, properties);
        this.valuesCopy = JSON.parse(JSON.stringify(this.values));
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/_variables.scss';

.tab-col-withsidebar {
  width: 50%;
}
.tab-col-withoutsidebar {
  width: calc(50% - (#{$map-width__hidden} - #{$map-width__visible}) / 2 - 4px);
}
:deep(.v-expansion-panel-content__wrap) {
  padding: 0 !important;
}
</style>
